// Deterministic asset-code generators, extracted verbatim from AssetImportMapper.js
// so both the legacy manual mapper and the new Smart Asset Import module share one
// implementation (no duplicated logic).
//
// These produce the AssetPro template's auto-generated code columns:
//   - Location Code / Sub Location Code  -> generateLocationCode (4 chars)
//   - Asset Subtype Code                 -> generateAssetSubtypeCode (3 chars)
//
// The rules are purely positional on the input words; they never invent data beyond
// the characters present in the source name, and an empty input yields an empty code.

/** Extract only letters and digits from a string. */
export const extractAlphanumeric = (str) => {
  if (!str) return '';
  return str.toString().replace(/[^a-zA-Z0-9]/g, '');
};

/**
 * Generate a Location Code / Sub Location Code (exactly 4 characters, uppercase).
 * 1 word  -> first 4 chars
 * 2 words -> 3 of first + 1 of second
 * 3 words -> 2 of first + 1 of second + 1 of third
 * 4+ words-> first char of first 4 words
 * Padded with the last char if shorter than 4.
 */
export const generateLocationCode = (locationName) => {
  if (!locationName) return '';

  const cleaned = extractAlphanumeric(locationName);
  if (!cleaned) return '';

  const words = locationName.toString().split(/\s+/).filter(w => w.trim());
  if (words.length === 0) return '';

  const alphanumericWords = words.map(w => extractAlphanumeric(w)).filter(w => w);
  if (alphanumericWords.length === 0) return '';

  let code = '';
  if (alphanumericWords.length === 1) {
    code = alphanumericWords[0].substring(0, 4).toUpperCase();
  } else if (alphanumericWords.length === 2) {
    const first = alphanumericWords[0].substring(0, 3);
    const second = alphanumericWords[1].substring(0, 1);
    code = (first + second).toUpperCase();
  } else if (alphanumericWords.length === 3) {
    const first = alphanumericWords[0].substring(0, 2);
    const second = alphanumericWords[1].substring(0, 1);
    const third = alphanumericWords[2].substring(0, 1);
    code = (first + second + third).toUpperCase();
  } else {
    code = alphanumericWords.slice(0, 4).map(w => w.substring(0, 1)).join('').toUpperCase();
  }

  if (code.length < 4) {
    const lastChar = code[code.length - 1] || 'X';
    code = code.padEnd(4, lastChar);
  } else if (code.length > 4) {
    code = code.substring(0, 4);
  }

  return code;
};

/**
 * Generate an Asset Subtype Code (exactly 3 characters, uppercase).
 * 1 word  -> first 3 chars
 * 2 words -> 2 of first + 1 of second
 * 3+ words-> first char of first 3 words
 * Padded with the last char if shorter than 3.
 */
export const generateAssetSubtypeCode = (subtypeName) => {
  if (!subtypeName) return '';

  const cleaned = extractAlphanumeric(subtypeName);
  if (!cleaned) return '';

  const words = subtypeName.toString().split(/\s+/).filter(w => w.trim());
  if (words.length === 0) return '';

  const alphanumericWords = words.map(w => extractAlphanumeric(w)).filter(w => w);
  if (alphanumericWords.length === 0) return '';

  let code = '';
  if (alphanumericWords.length === 1) {
    code = alphanumericWords[0].substring(0, 3).toUpperCase();
  } else if (alphanumericWords.length === 2) {
    const first = alphanumericWords[0].substring(0, 2);
    const second = alphanumericWords[1].substring(0, 1);
    code = (first + second).toUpperCase();
  } else {
    code = alphanumericWords.slice(0, 3).map(w => w.substring(0, 1)).join('').toUpperCase();
  }

  if (code.length < 3) {
    const lastChar = code[code.length - 1] || 'X';
    code = code.padEnd(3, lastChar);
  } else if (code.length > 3) {
    code = code.substring(0, 3);
  }

  return code;
};
